import {
  createFormatAdapter,
  createPlugin,
  wireHelper,
  type ParseGroupHook,
  type SerializeGroupHook,
} from '@customerio/dryad';
import {
  createDefaultAttributeFilterState,
  isTwoValueFilterOperator,
  isUnaryFilterOperator,
  validateAttributeFilterState,
  type AttributeFilterState,
} from '../../attribute-filter-state';
import {
  groupsForFamilies,
  isSupportedOperator,
  type AttributeFieldPolicy,
  type GroupableFamily,
  type OperatorGroup,
} from '../../operator-groups';
import { isRecord } from '@/utils/objects';

export const COLLECTION_ATTRIBUTE_CONDITION_TYPE = 'collection_attribute';
export const FLAT_FILTER_FORMAT = 'flat-filter';

/**
 * Every family a collection attribute condition can carry.
 *
 * Wider than the people-search surface on purpose: collections executes its
 * filters through the collections query path rather than the bitmap index, so
 * the inequality, string and timestamp operators all reach the backend.
 *
 * Collections does get the email family, despite what a sibling dryad surface
 * suggests: the legacy editor mounts CioConditionEditor through the
 * compact/collection branch with allowedOperators undefined, so it reaches the
 * full default groups (ui's COLLECTION_OPERATOR_FAMILIES declares 'email'), and
 * services accepts email_valid for collection_attribute — operatorIsSingleValue
 * lists it, decodeOperator handles the '!' prefix.
 */
export const COLLECTION_OPERATOR_FAMILIES = [
  'equality',
  'inequality',
  'existence',
  'string',
  'email',
  'dateExistence',
  'dateInequality',
  'extraDateInequality',
] as const satisfies readonly GroupableFamily[];

/**
 * The email operators stay pinned to the literal 'email' column, matching both
 * the legacy editor and this surface's behaviour before the shared vocabulary
 * landed. `one_of` is not in this surface's families at all, so the policy's
 * other two rules have nothing to constrain and are left unset.
 */
const COLLECTION_ATTRIBUTE_FIELD_POLICY: AttributeFieldPolicy = { emailField: 'email' };

export function attributeConditionOperatorGroupsForField(field: string): OperatorGroup[] {
  return groupsForFamilies(COLLECTION_OPERATOR_FAMILIES, field, COLLECTION_ATTRIBUTE_FIELD_POLICY);
}

/**
 * The groups with no field in hand. Used only for label lookup, which ignores
 * the disabled flag — the email operators come back disabled here, since the
 * policy pins them to the literal 'email' column.
 */
export const attributeConditionOperatorGroups: OperatorGroup[] = attributeConditionOperatorGroupsForField('');

export function isCollectionAttributeFilter(json: unknown): json is Record<string, unknown> & {
  type: typeof COLLECTION_ATTRIBUTE_CONDITION_TYPE;
  field: string;
  operator: string;
} {
  if (!isRecord(json) || json.type !== COLLECTION_ATTRIBUTE_CONDITION_TYPE) {
    return false;
  }
  if (typeof json.field !== 'string' || typeof json.operator !== 'string') {
    return false;
  }
  // Matched whole, not with a leading '!' stripped: isSupportedOperator reads
  // the declared negations from the vocabulary table, so a fabrication like
  // '!gt' — which has no negated form and no row to render it — is declined.
  return isSupportedOperator(json.operator, COLLECTION_OPERATOR_FAMILIES, false);
}

function conditionValue(operator: string, value: unknown): string | null {
  if (isUnaryFilterOperator(operator) || value == null) {
    return null;
  }
  return String(value);
}

function secondConditionValue(operator: string, value: unknown): string | null {
  return isTwoValueFilterOperator(operator) && value != null ? String(value) : null;
}

function toState(json: unknown): AttributeFilterState | null {
  if (!isCollectionAttributeFilter(json)) {
    return null;
  }
  const operator = json.operator;
  return {
    field: json.field,
    operator,
    value: conditionValue(operator, json.value),
    value2: secondConditionValue(operator, json.value2),
  };
}

function toWire(state: AttributeFilterState): Record<string, unknown> {
  const wire: Record<string, unknown> = {
    type: COLLECTION_ATTRIBUTE_CONDITION_TYPE,
    operator: state.operator,
    field: state.field,
    value: state.value,
  };
  if (isTwoValueFilterOperator(state.operator)) {
    wire.value2 = state.value2;
  }
  return wire;
}

// Shape-only validation from the shared vocabulary. Dryad wants plain strings
// here, while the shared validator tags each issue with the state property it
// concerns; collections shows no inline per-field errors yet, so the tags are
// dropped and only the messages kept.
function validateAttributeFilter(state: AttributeFilterState): string[] {
  return validateAttributeFilterState(state, COLLECTION_ATTRIBUTE_FIELD_POLICY).map((issue) => issue.message);
}

export function isValidAttributeFilterState(state: AttributeFilterState): boolean {
  return validateAttributeFilter(state).length === 0;
}

export function isCommittableCollectionAttributeFilter(json: unknown): boolean {
  const state = toState(json);
  return state != null && isValidAttributeFilterState(state);
}

export const collectionAttributePlugin = createPlugin({
  type: COLLECTION_ATTRIBUTE_CONDITION_TYPE,
  label: 'Attribute',
  parse: toState,
  serialize: toWire,
  validate: validateAttributeFilter,
  createDefault: createDefaultAttributeFilterState,
});

// The wire is a flat AND: services decodes conditions with js.Array() and has
// no group case, so any {and|or: [...]} envelope is a 422 in both directions.
// An {and: [...]} input is still unwrapped for the URLs that carry one. An
// {or: [...]} is declined here, so dryad's built-in grammar (which runs after
// plugin claiming) reads the envelope: the children survive as individual rows
// and the envelope is dropped on serialize — pinned by dryad-round-trip.test.ts.
const flatFilterParseGroup: ParseGroupHook = (json, builtInGroup) => {
  if (Array.isArray(json)) {
    return { operator: 'and', children: json };
  }
  const group = builtInGroup(json);
  return group?.operator === 'and' ? group : null;
};

const flatFilterSerializeGroup: SerializeGroupHook = (_operator, children) => children;

export const flatFilterAdapter = createFormatAdapter({
  plugins: [collectionAttributePlugin],
  // The flat wire is a bare array of leaf wires; members stay unknown because
  // error-node passthrough re-emits whatever arrived.
  wire: wireHelper<unknown[]>(),
  parseGroup: flatFilterParseGroup,
  serializeGroup: flatFilterSerializeGroup,
});

export type CollectionFilterBuilderMeta = {
  isDisabled?: boolean;
  schema: string[];
};

// Every envelope key the wire has ever used, 'or' included — unlike the parse
// grammar, which deliberately knows only about flat arrays and 'and'. The two
// are different questions: this one asks whether a param carries any condition
// worth sending, and an empty group carries none whatever it is spelled.
const GROUP_KEYS = ['and', 'or', 'not'];

export function isEmptyFilters(filters: unknown): boolean {
  if (Array.isArray(filters)) {
    return filters.length === 0 || filters.every(isEmptyFilters);
  }
  if (!isRecord(filters)) {
    return false;
  }
  const groupKey = GROUP_KEYS.find((key) => key in filters);

  return groupKey == null ? Object.keys(filters).length === 0 : isEmptyFilters(filters[groupKey]);
}
